import _ from 'lodash';
import {
  ArchivalUnitCopy,
  ArchivalAidMetadata,
  EadNode,
  EadNodeParentView,
} from 'types';
import { GridBreadcrumb } from '../grid-types';
import { variantToPath } from './VariantCtx';
import { getNodeId } from './ctx-utils';

export const createArchivalUnitHierarchyPath = (
  item: ArchivalUnitCopy,
  variant: 'funds' | 'aids'
) => {
  const path = variantToPath[variant!];
  const archivalCollection = item?.archivalUnitReference.archivalCollection;
  const aid = item.archivalUnitReference.describingArchivalAids?.[0];
  const crumbs: GridBreadcrumb[] =
    variant === 'funds'
      ? [
          {
            id: archivalCollection?.id ?? '',
            index: 0,
            label: archivalCollection?.name ?? '',
            to: `/${path}/0/${archivalCollection?.id ?? ''}`,
            level: 'fonds',
            timeRange: archivalCollection?.timeRange ?? '',
            levelLabel: 'fond',
          },
          {
            id: aid.id,
            index: 1,
            label: aid.name ?? aid.title ?? '',
            to: `/${path}/1/${aid?.id ?? ''}`,
            level: 'aid',
            timeRange: aid?.timeRange ?? '',
            levelLabel: 'pomůcka',
          },
          {
            id: item.id,
            index: 2,
            label: item.label ?? item.name ?? item.title ?? '',
            to: `/${path}/2/${item?.id ?? ''}`,
            level: 'item',
            timeRange: item.timeRange ?? '',
            levelLabel: 'archiválie',
          },
        ]
      : [
          {
            id: aid.id,
            index: 0,
            label: aid.name ?? aid.title ?? '',
            to: `/${path}/0/${aid?.id ?? ''}`,
            level: 'aid',
            timeRange: aid?.timeRange ?? '',
            levelLabel: 'pomůcka',
          },
          {
            id: item.id,
            index: 1,
            label: item.label ?? item.name ?? item.title ?? '',
            to: `/${path}/1/${item?.id ?? ''}`,
            level: 'item',
            timeRange: item.timeRange ?? '',
            levelLabel: 'archiválie',
          },
        ];
  return crumbs;
};

export const createAidHierarchyPath = (
  item: ArchivalAidMetadata,
  variant: 'funds' | 'aids'
) => {
  const path = variantToPath[variant!];
  const archivalCollection = item?.archivalCollection;
  const crumbs: GridBreadcrumb[] =
    variant === 'funds'
      ? [
          {
            id: archivalCollection?.id ?? '',
            index: 0,
            label: archivalCollection?.name ?? '',
            to: `/${path}/0/${archivalCollection?.id ?? ''}`,
            level: 'fonds',
            timeRange: archivalCollection?.timeRange ?? '',
            levelLabel: 'fond',
          },
          {
            id: item.id,
            index: 1,
            label: item.name ?? item.title ?? '',
            to: `/${path}/1/${item?.id ?? ''}`,
            level: 'aid',
            timeRange: item.timeRange ?? '',
            levelLabel: 'pomůcka',
          },
        ]
      : [
          {
            id: item.id,
            index: 0,
            label: item.name ?? item.title ?? '',
            to: `/${path}/0/${item?.id ?? ''}`,
            level: 'aid',
            timeRange: item.timeRange ?? '',
            levelLabel: 'pomůcka',
          },
        ];
  return crumbs;
};

export const createNestedEadHierarchyPath = (
  item: EadNode,
  variant: 'funds' | 'aids'
) => {
  const path = variantToPath[variant!];
  if (!item?.parent) {
    return;
  }
  const result: (EadNode | EadNodeParentView)[] = [];
  result.push({
    ...item,
    children: item.children ?? [],
  });
  let nextParent = item.parent;
  let fallback = 100;
  while (nextParent && fallback--) {
    if (_.get(nextParent, 'archivalAidMetadata')) {
      break;
    }
    result.push({
      ...nextParent,
    });

    if (nextParent.parent) {
      nextParent = nextParent.parent;
    } else {
      fallback = 0;
    }
  }

  if (_.get(nextParent, 'archivalAidMetadata')) {
    const aid = _.get(nextParent, 'archivalAidMetadata') as EadNodeParentView;
    result.push({
      ...aid,
      children: nextParent.children,
    });

    const collectionId = (_.get(aid, 'archivalCollection') as EadNodeParentView)
      .id;
    result.push({
      ...nextParent,
      id: collectionId,
    });
  }
  const cheap: Record<string, EadNode[]> = {};
  result.forEach((r, index) => {
    if (
      index - 1 >= 0 &&
      !(r.children as EadNode[])?.some?.(
        (f) => getNodeId(f) === getNodeId(result?.[index - 1])
      )
    ) {
      cheap[getNodeId(r)!] = [result[index - 1] as EadNode];
    } else {
      cheap[getNodeId(r)!] = (r.children ?? []) as EadNode[];
    }
  });

  if (variant === 'aids') {
    result.pop();
  }

  const AID_LEVEL = variant === 'aids' ? -1 : 1;
  const crumbs: GridBreadcrumb[] = result
    .map((r, index) => {
      const levelIndex = result.length - index - 1;
      const eadPostFix = levelIndex > AID_LEVEL ? '/ead' : '';
      return {
        id: getNodeId(r)!,
        index: levelIndex,
        label: r.label ?? r.title ?? '?',
        to: `/${path}/${levelIndex}/${getNodeId(r)!}${eadPostFix}`,
        level: r.level,
        timeRange: _.get(r, 'timeRange') ?? '',
        levelLabel: r.levelLabel ?? r.level ?? '',
      };
    })
    .reverse();

  if (variant === 'aids' && crumbs?.[0]) {
    crumbs[0].level = 'aid';
    crumbs[0].levelLabel = 'pomůcka';
  }

  if (variant === 'funds' && crumbs?.[1]) {
    crumbs[0].levelLabel = 'fond';
    crumbs[1].level = 'aid';
    crumbs[1].levelLabel = 'pomůcka';
  }

  return { crumbs, cheap };
};
